import React from 'react';
import { TFunction } from 'i18next';
import {
  DocumentationHistoryData,
  History,
  HistoryKindEnum,
} from 'graphql-common';
import Chip, { ChipTypes } from '@lib/components/Chip/Chip';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import styles from '../HistoryActivityTab.module.scss';
import getValidationStatusChipProps from './getValidationStatusChipProps';

export interface ActivityEntryInfo {
  actionLabel: string;
  indentBody: boolean;
  bodyContent: React.ReactNode | null;
  versionNumber?: number | null;
}

export default function getActivityEntryInfo(
  history: History,
  t: TFunction<'translation', undefined>,
): ActivityEntryInfo {
  const { kind, data } = history;
  const historyData = data as DocumentationHistoryData | null;

  if (
    (!historyData || kind === HistoryKindEnum.Created) &&
    historyData?.versionNumber == null
  ) {
    return {
      actionLabel: t('document-created'),
      indentBody: false,
      bodyContent: null,
      versionNumber: 1,
    };
  }

  if (historyData?.versionNumber != null) {
    return {
      actionLabel: t('version-updated'),
      indentBody: false,
      bodyContent: null,
      versionNumber: historyData.versionNumber[1],
    };
  }

  if (historyData?.validationStatus != null) {
    const [prevStatus, newStatus] = historyData.validationStatus;
    const prevChip = getValidationStatusChipProps(prevStatus);
    const newChip = getValidationStatusChipProps(newStatus);
    return {
      actionLabel: t('status-changed'),
      indentBody: true,
      bodyContent: (
        <div className={styles.historyValues}>
          {prevChip && (
            <Chip
              leftIcon="circle"
              label={t(prevChip.labelKey)}
              type={prevChip.color}
              strikethrough={newChip !== null}
            />
          )}
          {prevChip && newChip && (
            <MaterialIcon
              icon="arrow_forward"
              size="md-16"
              className={styles.historyValuesArrow}
            />
          )}
          {newChip && (
            <Chip
              leftIcon="circle"
              label={t(newChip.labelKey)}
              type={newChip.color}
            />
          )}
        </div>
      ),
    };
  }

  if (historyData?.approverId != null) {
    const [prevId, newId] = historyData.approverId;
    let actionLabel = t('approver-assigned');
    if (prevId && !newId) actionLabel = t('approver-removed');
    else if (prevId && newId) actionLabel = t('approver-changed');
    return {
      actionLabel,
      indentBody: true,
      bodyContent: null,
    };
  }

  if (historyData?.renewalDeadlineDate != null) {
    const [prevDate, newDate] = historyData.renewalDeadlineDate;
    const formattedPrev = prevDate ? getFormattedDate(prevDate) : null;
    const formattedNew = newDate ? getFormattedDate(newDate) : null;

    let actionLabel: string;
    let bodyLabel: string | null = null;
    if (!prevDate && newDate) {
      actionLabel = t('renewal-reminder-enabled');
      bodyLabel = t('next-deadline-date');
    } else if (prevDate && !newDate) {
      actionLabel = t('renewal-reminder-disabled');
    } else {
      actionLabel = t('renewal-date-updated');
    }

    return {
      actionLabel,
      indentBody: true,
      bodyContent: (
        <div className={styles.activityBody}>
          {bodyLabel && (
            <Typography variant="caption" className={styles.historyValuesTitle}>
              {bodyLabel}
            </Typography>
          )}
          <div className={styles.historyValues}>
            {formattedPrev && (
              <Chip
                leftIcon="calendar_today"
                label={formattedPrev}
                type={ChipTypes.chipWhite}
                strikethrough={formattedNew !== null}
              />
            )}
            {formattedPrev && formattedNew && (
              <MaterialIcon
                icon="arrow_forward"
                size="md-16"
                className={styles.historyValuesArrow}
              />
            )}
            {formattedNew && (
              <Chip
                leftIcon="calendar_today"
                label={formattedNew}
                type={ChipTypes.chipWhite}
              />
            )}
          </div>
        </div>
      ),
    };
  }

  if (historyData?.renewalSkipped != null) {
    const [deadlineDate] = historyData.renewalSkipped;
    const formatted = deadlineDate ? getFormattedDate(deadlineDate) : '';
    return {
      actionLabel: t('renewal-skipped'),
      indentBody: true,
      bodyContent: (
        <div className={styles.historyValues}>
          <Chip
            leftIcon="calendar_today"
            label={formatted}
            type={ChipTypes.chipWhite}
            strikethrough
          />
        </div>
      ),
    };
  }

  if (historyData?.renewalOverdue != null) {
    const [deadlineDate] = historyData.renewalOverdue;
    const formatted = deadlineDate ? getFormattedDate(deadlineDate) : '';
    return {
      actionLabel: t('update-overdue'),
      indentBody: true,
      bodyContent: (
        <div className={styles.activityBody}>
          <Typography variant="caption" className={styles.historyValuesTitle}>
            {t('deadline-date')}
          </Typography>
          <div className={styles.historyValues}>
            <Chip
              leftIcon="schedule"
              label={formatted}
              type={ChipTypes.chipWhite}
              strikethrough
            />
          </div>
        </div>
      ),
    };
  }

  return {
    actionLabel: t('edited'),
    indentBody: false,
    bodyContent: null,
  };
}
